import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreatePettyCashVoucherMutation, useUploadVoucherReceiptMutation } from "../../pettyCashApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const today = () => new Date().toISOString().slice(0, 10);

// Category and Receipt Number are deliberately NOT asked here (per direct
// instruction, 2026-08-29) - the requester only states what was spent and
// why; the payer is the one who picks a category, records the receipt
// number, attaches the scanned receipt, and states who was actually paid,
// all together at Pay time (see VoucherDetail.jsx's Pay section). Fund is
// resolved automatically server-side from the branch.
const VoucherCreate = () => {
  const navigate = useNavigate();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createVoucher, { isLoading }] = useCreatePettyCashVoucherMutation();
  const [uploadReceipt] = useUploadVoucherReceiptMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [expenseDate, setExpenseDate] = useState(today());
  const [description, setDescription] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [supplierName, setSupplierName] = useState("");
  // Optional receipt / document at request time (2026-09-22). The payer can still attach
  // or replace it at Pay time as before - this only lets the requester start with one.
  const [file, setFile] = useState(null);

  const handleSave = async () => {
    if (availableBranches.length > 1 && !branchId) return toast.error("Branch is required");
    if (!description.trim()) return toast.error("Description is required");
    if (!requestedAmount || Number(requestedAmount) <= 0) return toast.error("Enter a valid amount");
    if (file && file.size > 1024 * 1024) return toast.error("The receipt must be 1MB or smaller - please compress the photo");
    try {
      const voucher = await createVoucher({
        branchId: branchId ? Number(branchId) : undefined,
        expenseDate,
        description: description.trim(),
        requestedAmount: Number(requestedAmount),
        supplierName: supplierName || undefined,
      }).unwrap();
      toast.success("Voucher submitted");
      if (file) {
        try {
          await uploadReceipt({ recordGuid: voucher.recordGuid, file }).unwrap();
        } catch (uploadError) {
          toast.error(uploadError?.message || "The voucher was submitted but the receipt could not be attached - add it from the voucher page.");
        }
      }
      navigate(paths.pettyCashVoucherDetail(voucher.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not submit the voucher");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashVouchers} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Vouchers
              </Link>
              <h4>New Voucher</h4>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Voucher Details</h5>
            <div className="row">
              {availableBranches.length > 1 && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Branch</label>
                  <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-4 mb-3">
                <label className="form-label">Expense Date</label>
                <input type="date" className="form-control" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Requested Amount</label>
                <input type="number" className="form-control" value={requestedAmount} onChange={(e) => setRequestedAmount(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Supplier (optional)</label>
                <input type="text" className="form-control" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Receipt / document (optional)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  className="form-control"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <div className="form-text">JPEG, PNG or PDF, up to 1MB. The payer can still attach or replace it at Pay time.</div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isLoading}>Submit</button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.pettyCashVouchers)}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherCreate;
